<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import AppCard from './ui/AppCard.vue'
import UserSubOrgCell from './UserSubOrgCell.vue'
import LifecycleStatusBadge from './LifecycleStatusBadge.vue'

const props = defineProps({
  users: {
    type: Array,
    default: () => [],
  },
  selectedUsers: {
    type: Object,
    default: () => new Set(),
  },
  onToggleUserSelection: {
    type: Function,
    default: null,
  },
  onToggleSelectAll: {
    type: Function,
    default: null,
  },
  getOrgName: {
    type: Function,
    default: () => 'Unknown',
  },
  highlightSubOrgId: {
    type: Number,
    default: null,
  },
  hasSubOrgAccess: {
    type: Function,
    default: null,
  },
  onManageSubOrgs: {
    type: Function,
    default: null,
  },
  onDeleteUser: {
    type: Function,
    default: null,
  },
  deletingUserIds: {
    type: Object,
    default: () => new Set(),
  },
  updatingUserIds: {
    type: Object,
    default: () => new Set(),
  },
  actionsDisabled: {
    type: Boolean,
    default: false,
  },
  isUserReadOnly: {
    type: Function,
    default: () => false,
  },
  getUserReadOnlyReason: {
    type: Function,
    default: () => '',
  },
  getUserOrg: {
    type: Function,
    default: () => null,
  },
  getUserDeletedMembershipCount: {
    type: Function,
    default: () => 0,
  },
  emptyMessage: {
    type: String,
    default: 'No users found.',
  },
})

const isMobile = ref(false)
let mobileResizeHandler = null

const syncIsMobile = () => {
  if (typeof window === 'undefined') return
  isMobile.value = window.innerWidth <= 768 || window.innerHeight <= 760
}

onMounted(() => {
  syncIsMobile()
  mobileResizeHandler = () => syncIsMobile()
  window.addEventListener('resize', mobileResizeHandler)
})

onBeforeUnmount(() => {
  if (mobileResizeHandler) window.removeEventListener('resize', mobileResizeHandler)
})

const getUserDisplayName = (user) => {
  const fullName = String(user?.full_name ?? '').trim()
  if (fullName) return fullName
  const username = String(user?.username ?? '').trim()
  if (username) return username
  return `User #${user?.id ?? ''}`
}

const getSubOrgSummary = (user) => {
  const ids = Array.isArray(user?.sub_org_ids) ? user.sub_org_ids : []
  if (!ids.length) return '-'
  return ids.length > 2 ? `${ids.slice(0, 2).join(', ')} +${ids.length - 2}` : ids.join(', ')
}

const isUserBusy = (userId) => {
  return props.actionsDisabled
    || props.deletingUserIds.has(Number(userId))
    || props.updatingUserIds.has(Number(userId))
}

const isReadOnlyUser = (user) => props.isUserReadOnly(user)
const getReadOnlyReason = (user) => props.getUserReadOnlyReason(user)
</script>

<template>
  <div class="user-table-wrap">
    <div v-if="isMobile && users.length" class="users-mobile-list mobile-users-list">
      <div class="mobile-actions-row panel">
        <label class="mobile-select-all">
          <input type="checkbox" :disabled="actionsDisabled" @change="onToggleSelectAll && onToggleSelectAll(users)" />
          Select all
        </label>
      </div>

      <AppCard
        v-for="u in users"
        :key="u.id"
        class="mobile-card user-card"
        :class="{
          'membership-highlight': highlightSubOrgId !== null && hasSubOrgAccess && hasSubOrgAccess(u, highlightSubOrgId),
          'read-only-user-row': isReadOnlyUser(u),
        }"
      >
        <div class="mobile-card-header">
          <label class="mobile-row-check">
            <input
              type="checkbox"
              :checked="selectedUsers.has(u.id)"
              :disabled="isUserBusy(u.id)"
              @change="onToggleUserSelection && onToggleUserSelection(u.id)"
            />
          </label>

          <div class="mobile-card-title-wrap">
            <div class="mobile-card-title">{{ getUserDisplayName(u) }}</div>
            <div class="mobile-card-subtitle">{{ u.email }}</div>
          </div>
        </div>

        <div class="mobile-card-meta">
          <div><strong>Role:</strong> <span class="badge">{{ u.role }}</span></div>
          <div>
            <strong>Org:</strong> {{ getOrgName(u.organization_id) }}
            <LifecycleStatusBadge :entity="getUserOrg(u)" />
          </div>
          <div><strong>SubOrg:</strong> {{ getSubOrgSummary(u) }}</div>
          <div v-if="getUserDeletedMembershipCount(u) > 0" class="lifecycle-note">
            {{ getUserDeletedMembershipCount(u) }} deleted membership(s) (read-only)
          </div>
          <div><strong>Org ID:</strong> {{ u.organization_id }}</div>
          <div><strong>Phone:</strong> {{ u.phone || '-' }}</div>
          <div><strong>Password:</strong> {{ u.must_change_password ? 'Yes' : 'No' }}</div>
        </div>

        <p v-if="isReadOnlyUser(u)" class="lifecycle-note">{{ getReadOnlyReason(u) }}</p>

        <div class="mobile-card-actions">
          <button v-if="onManageSubOrgs" type="button" class="btn btn-primary" :disabled="isUserBusy(u.id) || isReadOnlyUser(u)" @click="onManageSubOrgs(u)">
            Edit
          </button>
          <button v-if="onDeleteUser" type="button" class="btn btn-danger" :disabled="isUserBusy(u.id) || isReadOnlyUser(u)" @click="onDeleteUser(u.id)">
            {{ deletingUserIds.has(u.id) ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </AppCard>
    </div>

    <table v-else-if="users.length" class="users-table table table-wrapper">
      <thead>
        <tr>
          <th>
            <input
              type="checkbox"
              :disabled="actionsDisabled"
              @change="onToggleSelectAll && onToggleSelectAll(users)"
            />
          </th>
          <th>ID</th>
          <th>Username</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Role</th>
          <th>Organization</th>
          <th>SubOrgs</th>
          <th>Org ID</th>
          <th>Must Change Password</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="u in users"
          :key="u.id"
          :class="{
            'membership-highlight': highlightSubOrgId !== null && hasSubOrgAccess && hasSubOrgAccess(u, highlightSubOrgId),
            'read-only-user-row': isReadOnlyUser(u),
          }"
        >
          <td>
            <input
              type="checkbox"
              :checked="selectedUsers.has(u.id)"
              :disabled="isUserBusy(u.id) || isReadOnlyUser(u)"
              @change="onToggleUserSelection && onToggleUserSelection(u.id)"
            />
          </td>
          <td>{{ u.id }}</td>
          <td>{{ u.username }}</td>
          <td>{{ u.email }}</td>
          <td>{{ u.phone }}</td>
          <td>{{ u.role }}</td>
          <td>
            {{ getOrgName(u.organization_id) }}
            <LifecycleStatusBadge :entity="getUserOrg(u)" />
            <p v-if="isReadOnlyUser(u)" class="lifecycle-note">{{ getReadOnlyReason(u) }}</p>
          </td>
          <td>
            <UserSubOrgCell :userId="u.id" />
            <p v-if="getUserDeletedMembershipCount(u) > 0" class="lifecycle-note">
              {{ getUserDeletedMembershipCount(u) }} deleted membership(s)
            </p>
          </td>
          <td>{{ u.organization_id }}</td>
          <td>{{ u.must_change_password ? 'Yes' : 'No' }}</td>
          <td class="actions-bar">
            <button v-if="onManageSubOrgs" type="button" class="btn btn-primary" :disabled="isUserBusy(u.id) || isReadOnlyUser(u)" @click="onManageSubOrgs(u)">
              Manage User
            </button>
            <button v-if="onDeleteUser" type="button" class="btn btn-danger" :disabled="isUserBusy(u.id) || isReadOnlyUser(u)" @click="onDeleteUser(u.id)">
              {{ deletingUserIds.has(u.id) ? 'Deleting...' : 'Delete' }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <p v-else class="empty">{{ emptyMessage }}</p>
  </div>
</template>

<style scoped>
.user-table-wrap {
  width: 100%;
}

.users-mobile-list {
  display: block;
}

.users-table {
  display: table;
}

.table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.table-wrapper {
  overflow-x: auto;
}

th,
td {
  border: 1px solid #e5e7eb;
  padding: 0.55rem;
  text-align: left;
  color: #111827;
}

th {
  background: #f3f4f6;
}

.mobile-users-list {
  display: grid;
  gap: 0.75rem;
}

.mobile-actions-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mobile-select-all {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #111827;
  font-weight: 600;
}

.mobile-card-title-wrap {
  display: grid;
  gap: 0.2rem;
}

.mobile-card-title {
  font-weight: 700;
  color: #111827;
  font-size: 1rem;
}

.mobile-card-subtitle {
  color: #6b7280;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
}

.mobile-card-meta {
  display: grid;
  gap: 0.35rem;
  color: #111827;
  font-size: 0.95rem;
}

.mobile-card-meta strong {
  color: #374151;
}

.mobile-card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.mobile-card-actions .btn {
  flex: 1 1 120px;
}

.empty {
  padding: 1rem;
  background: #fff;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  color: #6b7280;
}

.badge {
  padding: 0.15rem 0.4rem;
  border-radius: 9999px;
  background: #e5e7eb;
  color: #111827;
  font-size: 0.75rem;
  line-height: 1.2;
}

.membership-highlight {
  background: #f0f9ff;
}

.read-only-user-row {
  background: #fff7ed;
}

.lifecycle-note {
  margin: 0.35rem 0 0;
  color: #92400e;
  font-size: 0.8rem;
}

.mobile-card-header {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.mobile-row-check {
  display: flex;
  align-items: center;
  min-height: 44px;
}

@media (max-width: 768px), (max-height: 760px) {
  .users-table {
    display: none;
  }

  .users-mobile-list {
    display: block;
  }

  .table-wrapper {
    overflow-x: auto;
  }
}

@media (min-width: 900px) {
  .users-mobile-list {
    display: none;
  }
}
</style>
